/**
 * A thread's pinned opening message, held to a few lines so a long first ask
 * doesn't push the replies off the panel. Show more opens it in full, capped
 * with its own scroll so the replies and the composer stay in reach.
 */
import { cn } from "@crew/ui/lib/utils";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { clampHidesText } from "./clamp";

export function ClampedText({
  text,
  clamp,
  className,
}: {
  text: string;
  /** The clamped height in whole lines, a `max-h-[4lh]` class. */
  clamp: string;
  className: string;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const inner = useRef<HTMLSpanElement>(null);
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const [hides, setHides] = useState(false);
  // Measured only while clamped: open, it keeps the toggle it opened with.
  // The clamp holds the paragraph's height, so a rewrap (a narrower panel, a
  // font loading) shows only in the unclamped text inside it.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !inner.current || expanded) return;
    const measure = () => setHides(clampHidesText(el));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inner.current);
    return () => observer.disconnect();
  }, [text, clamp, expanded]);
  const toggle = () => {
    // A collapsed opener starts at its first line, wherever it was scrolled open.
    if (expanded && ref.current) ref.current.scrollTop = 0;
    setExpanded(!expanded);
  };
  return (
    <>
      <p
        ref={ref}
        id={id}
        data-clamped={expanded ? "open" : "closed"}
        tabIndex={expanded ? 0 : undefined}
        className={cn(
          "whitespace-pre-wrap text-foreground",
          expanded
            ? "max-h-[40dvh] overflow-y-auto overscroll-contain"
            : cn(
                "overflow-hidden",
                clamp,
                hides && "[mask-image:linear-gradient(to_bottom,#000_calc(100%-1lh),transparent)]",
              ),
          className,
        )}
      >
        <span ref={inner} className="block">
          {text}
        </span>
      </p>
      {hides ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={toggle}
          className="cursor-pointer self-start rounded-sm text-meta text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </>
  );
}
